import { Controller } from "@hotwired/stimulus"

// Connects to data-controller="search"
// Debounces search input and updates a Turbo Frame with results
export default class extends Controller {
  static targets = ["input", "filter", "checkbox", "memberCheckbox", "providerCheckbox"]
  static values = {
    url: String,
    frameId: { type: String, default: "search-results" },
    minLength: { type: Number, default: 3 },
    debounce: { type: Number, default: 300 },
    paramName: { type: String, default: "search" },
    filterParam: { type: String, default: "status" },
    checkboxParam: { type: String, default: "categories" }
  }

  connect() {
    this.timeout = null
  }

  disconnect() {
    if (this.timeout) {
      clearTimeout(this.timeout)
    }
  }

  search() {
    const query = this.inputTarget.value.trim()

    // Clear existing timeout
    if (this.timeout) {
      clearTimeout(this.timeout)
    }

    // Debounce the search
    this.timeout = setTimeout(() => {
      this.performSearch(query)
    }, this.debounceValue)
  }

  // Called when filter changes
  filter() {
    const query = this.hasInputTarget ? this.inputTarget.value.trim() : ""
    this.performSearch(query)
  }

  performSearch(query) {
    // If query is empty or below min length, search with empty query to show all
    const searchQuery = query.length >= this.minLengthValue ? query : ""
    
    const url = new URL(this.urlValue, window.location.origin)
    if (searchQuery) {
      url.searchParams.set(this.paramNameValue, searchQuery)
    }

    // Add filter value if filter target exists (select dropdown)
    if (this.hasFilterTarget) {
      const filterValue = this.filterTarget.value
      if (filterValue && filterValue !== "all") {
        url.searchParams.set(this.filterParamValue, filterValue)
      }
    }

    // Add checkbox values if checkbox targets exist (multiple checkboxes)
    if (this.hasCheckboxTarget) {
      const checkedValues = this.checkboxTargets
        .filter(checkbox => checkbox.checked)
        .map(checkbox => checkbox.value)
      
      checkedValues.forEach(value => {
        url.searchParams.append(this.checkboxParamValue + "[]", value)
      })
    }

    // Add member checkbox values if member checkbox targets exist
    if (this.hasMemberCheckboxTarget) {
      const checkedValues = this.memberCheckboxTargets
        .filter(checkbox => checkbox.checked)
        .map(checkbox => checkbox.value)

      checkedValues.forEach(value => {
        url.searchParams.append("members[]", value)
      })
    }

    // Add provider checkbox values if provider checkbox targets exist
    if (this.hasProviderCheckboxTarget) {
      const checkedValues = this.providerCheckboxTargets
        .filter(checkbox => checkbox.checked)
        .map(checkbox => checkbox.value)

      checkedValues.forEach(value => {
        url.searchParams.append("providers[]", value)
      })
    }

    // Find the Turbo Frame and update its src
    const frame = document.getElementById(this.frameIdValue)
    if (frame) {
      frame.src = url.toString()
    }
  }

  // Clear search and show all results
  clear() {
    this.inputTarget.value = ""
    this.performSearch("")
  }

  // Called when a checkbox changes
  checkboxChanged() {
    const query = this.hasInputTarget ? this.inputTarget.value.trim() : ""
    this.performSearch(query)
  }
}
